import DT from '@repo/alt/DependencyTypes'
import { SystemEvents } from '@repo/alt/Services/constants'
import { AppApiGateway } from '@repo/api/web/AppApiGateway'
import type { ConfirmChallengeRequest, SignInRequest, SignUpRequest } from '@repo/api/web/models'
import { inject, injectable } from 'inversify'
import type { Emitter } from 'nanoevents'

import { AuthCookieService } from './Auth.CookieService'

import { useAuthStore } from '#Auth/store'
import { useCartStore } from '#Cart/store'
import { usePromotionStore } from '#Promotion/store'
import { UserService } from '#User/services'

@injectable()
export class AuthService {
  private $store: ReturnType<typeof useAuthStore>
  protected cartStore: ReturnType<typeof useCartStore>
  protected promotionStore: ReturnType<typeof usePromotionStore>
  protected appConfig: ReturnType<typeof useAppConfig>

  constructor(
    @inject(AppApiGateway)
    readonly api: AppApiGateway,
    @inject(AuthCookieService)
    readonly $cookieService: AuthCookieService,
    @inject(UserService)
    readonly $userService: UserService,
    @inject(DT.EventBus)
    readonly $eventBus: Emitter,
  ) {
    this.$store = useAuthStore()
    this.cartStore = useCartStore()
    this.promotionStore = usePromotionStore()
    this.appConfig = useAppConfig()
  }

  login(model: SignInRequest) {
    return this.api.authSignIn(this.appConfig.host, model)
  }

  signUp(model: SignUpRequest) {
    return this.api.authSignUp(this.appConfig.host, model)
  }

  confirm(model: ConfirmChallengeRequest) {
    return this.api.authConfirm(this.appConfig.host, model)
  }

  logout() {
    return this.api.authSignOut(this.appConfig.host)
  }

  logoutAll() {
    return this.api.authSignOutAll(this.appConfig.host)
  }

  setToken(token: string) {
    this.$store.setToken(token)
    this.$cookieService.token.set(token)
  }

  emitLoggedIn() {
    this.$eventBus.emit(SystemEvents.LOGGED_IN)
  }

  async signIn(token: string) {
    await this.loadSession(token)

    await this.$userService.loadCurrentUser()

    this.emitLoggedIn()
  }

  getToken() {
    return this.$cookieService.token.get() || this.$store.token
  }

  checkSession() {
    return this.api.authCheckSession(this.appConfig.host)
  }

  async loadSession(token: string) {
    this.setToken(token)
    const info = await this.checkSession()
    this.$store.setSession(info)
  }

  getConfig() {
    return this.api.authAuthConfig(this.appConfig.host)
  }

  resetStore() {
    this.$store.reset()
  }

  resetCookie() {
    this.$cookieService.token.set(null)
  }

  signInAnonim() {
    return this.api.authSignInAnonym(this.appConfig.host)
  }

  async createAnonimSession() {
    const info = await this.signInAnonim()
    if (!info.token) return

    this.setToken(info.token)
    this.$store.setSession(info)
    await this.$userService.loadCurrentUser()
  }

  signOut() {
    this.resetStore()
    this.resetCookie()
    this.$userService.resetStore()
    this.cartStore.reset()
    this.promotionStore.resetPricing()
    this.$eventBus.emit(SystemEvents.LOGGED_OUT)
    this.api.authSignOut(this.appConfig.host)
  }
}
